import { App } from "@/constants";
import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import {
    customerInfoProviderToken,
    globalLoggerToken,
    growaveConfigurationToken,
    gwStorefrontAppInfoToken,
    localizationInfoProviderToken,
    modalManagerToken,
} from "@/tokens";
import { isRewardsPage } from "@apps/rewards/utils/isRewardsPage/isRewardsPage";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { MinilogLogger } from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { ROLE } from "@modules/authentication/constants";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CommonModule } from "@modules/common_module/CommonModule";
import { lambdaApiClientToken } from "@modules/customer_module/di_tokens";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import GrowaveStorefrontValuesModule from "@modules/growave_storefront_values/GrowaveStorefrontValuesModule";
import FakeMoneyExchanger from "@modules/money/MoneyExchanger/FakeMoneyExchanger";
import LegacyMoneyExchanger from "@modules/money/MoneyExchanger/LegacyMoneyExchanger";
import { MoneyModule } from "@modules/money/MoneyModule";
import { ProductsModule } from "@modules/products/ProductsModule";
import { SDK_MODULES } from "@modules/sdk/constants";
import { SdkModule } from "@modules/sdk/SdkModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import AttributesObserver from "@ui-utils/AttributesObserver/AttributesObserver";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    Observable,
    filter,
    lastValueFrom,
    map,
    of,
    shareReplay,
    take,
    tap,
} from "rxjs";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import DrawerManager from "@components/Drawer/DrawerManager";
import type { ApplicationInterface } from "@interfaces/ApplicationInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import { definitions } from "./constants/definitions";
import RewardsB2bCompanyService from "./modules/B2bCompany/service/RewardsB2bCompanyService";
import RewardsCustomerRewardsApiService from "./modules/CustomerRewards/api_service/RewardsCustomerRewardsApiService";
import RewardsEarningRulesApiService from "./modules/EarningRules/api_service/RewardsEarningRulesApiService";
import RewardsProgramAccountApiService from "./modules/ProgramAccount/api_service/RewardsProgramAccountApiService";
import RewardsProgramAccountService from "./modules/ProgramAccount/service/RewardsProgramAccountService";
import RewardsReferralContextService from "./modules/ReferralProgram/services/RewardsReferralContextService";
import RewardsSpendingRulesApiService from "./modules/SpendingRules/api_service/RewardsSpendingRulesApiService";
import {
    BRANDING_WIDGET_TYPES,
    PROGRAM_STATUS,
} from "./modules/StorefrontValues/constants";
import type { RewardsStorefrontValuesBrandingEntity } from "./modules/StorefrontValues/models/RewardsStorefrontValuesEntities";
import RewardsStorefrontValuesService from "./modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsVerificationTokenApiService from "./modules/VerificationToken/api_service/RewardsVerificationTokenApiService";
import RewardsVipTiersApiService from "./modules/VipTiers/api_service/RewardsVipTiersApiService";
import { subscribeToSdkMethods } from "./rewards_sdk/utils/subscribeToSdkMethods";
import type { RewardsApplicationConfigInterface } from "./RewardsApplicationConfig";
import {
    RewardsApplicationConfigSchema,
    RewardsApplicationConfigToken,
} from "./RewardsApplicationConfig";
import RewardsCustomerEligibilityService from "./services/RewardsCustomerEligibilityService/RewardsCustomerEligibilityService";
import RewardsDiResolverService from "./services/RewardsDiResolverService/RewardsDIResolverService";
import RewardsWidgetInjectionService from "./services/RewardsWidgetInjectionService/RewardsWidgetInjectionService";
import {
    drawerManagerToken,
    rewardsApplicationLoggerToken,
    rewardsAuthApiClientToken,
    rewardsAuthApiClientV3Token,
    rewardsMoneyExchangerToken,
} from "./tokens";
import { InjectorDefinition, WidgetInjectorParams } from "./types";
import { isPopUpWidgetLauncherVisible } from "./utils/isPopUpWidgetLauncherVisible/isPopUpWidgetLauncherVisible";
import { registerModals } from "./utils/modal/utils";
import { normalizeLocale } from "./utils/normalizeLocale/normalizeLocale";
import AvailableRewardsCartWidgetInjector from "./widgets/rewards_cart_widgets/rewards_available_rewards_cart_widget/AvailableRewardsCartWidgetInjector";
import RewardsEarnPointsCartWidgetInjector from "./widgets/rewards_cart_widgets/rewards_earn_points_cart_widget/RewardsEarnPointsCartWidgetInjector";
import RewardsDrawerWidgetInjector from "./widgets/rewards_drawer_widget/RewardsDrawerWidgetInjector";
import RewardsEarnPointsProductPageWidgetInjector from "./widgets/rewards_earn_points_product_page_widget/RewardsEarnPointsProductPageWidgetInjector";
import FreeProductSliderWidgetInjector from "./widgets/rewards_free_product_slider_widget/FreeProductSliderWidgetInjector";
import RewardsHubWidgetInjector from "./widgets/rewards_hub_widget/RewardsHubWidgetInjector";
import RewardsCustomerPointsBalanceInlineWidgetInjector from "./widgets/rewards_inline_widgets/rewards_customer_points_balance_inline_widget/RewardsCustomerPointsBalanceInlineWidgetInjector";
import RewardsJoinNowModalWidgetInjector from "./widgets/rewards_join_now_modal_widget/RewardsJoinNowModalWidgetInjector";
import RewardsLoyaltyInfoPageWidgetInjector from "./widgets/rewards_loyalty_info_page_widget/RewardsLoyaltyInfoPageWidgetInjector";
import RewardsAvailableRewardsPageWidgetModalInjector from "./widgets/rewards_page_widgets/widgets/rewards_available_rewards_page_widget_modal/RewardsAvailableRewardsPageWidgetModalInjector";
import RewardsAvailableRewardsPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_available_rewards_page_widget/RewardsAvailableRewardsPageWidgetInjector";
import RewardsAvailableStoreCreditPageWidgetModalInjector from "./widgets/rewards_page_widgets/widgets/rewards_available_store_credit_page_widget_modal/RewardsAvailableStoreCreditPageWidgetModalInjector";
import RewardsEmptyStatePageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_empty_state_page_widget/RewardsEmptyStatePageWidgetInjector";
import RewardsFaqPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_faq_page_widget/RewardsFaqPageWidgetInjector";
import RewardsHistoryPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_history_page_widget/RewardsHistoryPageWidgetInjector";
import RewardsReferralProgramPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_referral_program_page_widget/RewardsReferralProgramPageWidgetInjector";
import RewardsReferralsPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_referrals_page_widget/RewardsReferralsPageWidgetInjector";
import RewardsVipTiersPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_vip_tiers_page_widget/RewardsVipTiersPageWidgetInjector";
import RewardsWaysToEarnPageWidgetModalInjector from "./widgets/rewards_page_widgets/widgets/rewards_ways_to_earn_page_widget_modal/RewardsWaysToEarnPageWidgetModalInjector";
import RewardsWaysToEarnPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_ways_to_earn_page_widget/RewardsWaysToEarnPageWidgetInjector";
import RewardsWaysToSpendPageWidgetModalInjector from "./widgets/rewards_page_widgets/widgets/rewards_ways_to_spend_page_widget_modal/RewardsWaysToSpendPageWidgetModalInjector";
import RewardsWaysToSpendPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_ways_to_spend_page_widget/RewardsWaysToSpendPageWidgetInjector";
import RewardsPopUpWidgetInjector from "./widgets/rewards_popup_widget/RewardsPopUpWidgetInjector";
import RewardsQrCodeModalWidgetInjector from "./widgets/rewards_qr_code_modal_widget/RewardsQrCodeModalWidgetInjector";
import RewardsRefundDiscountModalWidgetInjector from "./widgets/rewards_refund_discount_modal_widget/RewardsRefundDiscountModalWidgetInjector";
import RewardsVerifyAccountModalWidgetInjector from "./widgets/rewards_verify_account_modal_widget/RewardsVerifyAccountModalWidgetInjector";
import RewardsVipTiersCardWidgetInjector from "./widgets/rewards_vip_tiers_card_widget/RewardsVipTiersCardWidgetInjector";
import RewardsVipTiersMultiProgressWidgetInjector from "./widgets/rewards_vip_tiers_multi_progress_widget/RewardsVipTiersMultiProgressWidgetInjector";
import RewardsVipTiersSingleProgressWidgetInjector from "./widgets/rewards_vip_tiers_single_progress_widget/RewardsVipTiersSingleProgressWidgetInjector";

@singleton()
export class RewardsApplication implements ApplicationInterface<object> {
    private readonly dynamicImportManager: DynamicImportManager;

    widgets: WidgetInterface[] = [];
    exports = {};

    constructor(
        @inject(RewardsApplicationConfigToken)
        public readonly config: RewardsApplicationConfigInterface,
        @inject(DynamicImportModule)
        dynamicImportModule: DynamicImportModule,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector,
        @inject(AttributesObserver)
        private readonly attributesObserver: AttributesObserver
    ) {
        assert(config, RewardsApplicationConfigSchema);

        this.dynamicImportManager =
            dynamicImportModule.exports.dynamicImportManager;

        definitions.forEach((definition) => {
            this.dynamicImportManager.register(definition);
        });
    }

    init(): this {
        const logger = new MinilogLogger("rewards");
        logger.debug("RewardsApplication is initialized", {
            ...this.config,
        });
        const diResolverService = new RewardsDiResolverService();
        const {
            baseModule,
            commonModule,
            analyticsEventsCollectorModule,
            tokenManagerModule,
            authenticationModule,
            growaveStorefrontValuesModule,
            productsModule,
            localizationInfoProvider,
            sdkModule,
            modalManager,
            customerInfoProvider,
        } = diResolverService.resolveAll({
            baseModule: BaseModule,
            analyticsEventsCollectorModule: AnalyticsEventsCollectorModule,
            commonModule: CommonModule,
            tokenManagerModule: TokenManagerModule,
            authenticationModule: AuthenticationModule,
            growaveStorefrontValuesModule: GrowaveStorefrontValuesModule,
            productsModule: ProductsModule,
            localizationInfoProvider: localizationInfoProviderToken,
            sdkModule: SdkModule,
            modalManager: modalManagerToken,
            customerInfoProvider: customerInfoProviderToken,
        });

        const { tokenManager } = tokenManagerModule.exports;
        const { eventsCollector, eventsFactory } =
            analyticsEventsCollectorModule.exports;
        const {
            designModeQueryParamsProvider,
            locationController,
            designModeProvider,
            urlsBuilder,
            baseUrlProvider,
        } = commonModule.exports;
        const { authenticationInfoProvider } = authenticationModule.exports;
        const { growaveStorefrontValuesService } =
            growaveStorefrontValuesModule.exports;
        const { productsService, productsQuery } = productsModule.exports;

        const isCustomer$ = authenticationInfoProvider.selectRole().pipe(
            map((role) => role === ROLE.Customer),
            shareReplay({
                bufferSize: 1,
                refCount: true,
            })
        );

        const isDesignMode = !!designModeProvider.isDesignMode();

        const isCustomerB2b = customerInfoProvider.getIsB2b();

        const injectorCommonConfig = {
            logger,
            eventsCollector,
            eventsFactory,
            dynamicImportManager: this.dynamicImportManager,
            mutationObserverInjector: this.mutationObserverInjector,
            diResolverService,
        };

        //Register modals
        registerModals(modalManager);

        //Register money exchanger
        container.register(rewardsMoneyExchangerToken, {
            useFactory: instanceCachingFactory((di) => {
                if (di.resolve(growaveConfigurationToken).initedFromSdk) {
                    return new FakeMoneyExchanger();
                }
                return new LegacyMoneyExchanger(
                    di.resolve(growaveConfigurationToken).shopCurrency,
                    di.resolve(MoneyModule).exports.gwCurrency
                );
            }),
        });

        //Register drawer manager
        container.registerInstance(drawerManagerToken, new DrawerManager());

        //Register rewardsAuthApiClient
        container.registerInstance(rewardsApplicationLoggerToken, logger);
        container.register(rewardsAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/rewards-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode,
                                ...designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(rewardsApplicationLoggerToken)
                        ),
                        eventsCollector,
                        locationController,
                        eventsFactory
                    ),
                    tokenManager
                );
            }),
        });

        container.register(rewardsAuthApiClientV3Token, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/rewards-storefront/v3"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode,
                                ...designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(rewardsApplicationLoggerToken)
                        ),
                        eventsCollector,
                        locationController,
                        eventsFactory
                    ),
                    tokenManager
                );
            }),
        });

        container.register(lambdaApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            baseUrlProvider.appendToBaseUrl("/lambda"),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode,
                                ...designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(globalLoggerToken)
                        ),
                        eventsCollector,
                        locationController,
                        eventsFactory
                    ),
                    tokenManager
                );
            }),
        });

        //Rewards services
        const {
            programAccountApiService,
            programAccountService,
            earningRulesApiService,
            spendingRulesApiService,
            customerRewardsApiService,
            vipTiersApiService,
            storefrontValuesService,
            customerEligibilityService,
            referralContextService,
            verificationTokenApiService,
            b2bCompanyService,
        } = diResolverService.resolveAll({
            programAccountApiService: RewardsProgramAccountApiService,
            programAccountService: RewardsProgramAccountService,
            earningRulesApiService: RewardsEarningRulesApiService,
            spendingRulesApiService: RewardsSpendingRulesApiService,
            customerRewardsApiService: RewardsCustomerRewardsApiService,
            vipTiersApiService: RewardsVipTiersApiService,
            storefrontValuesService: RewardsStorefrontValuesService,
            customerEligibilityService: RewardsCustomerEligibilityService,
            referralContextService: RewardsReferralContextService,
            verificationTokenApiService: RewardsVerificationTokenApiService,
            b2bCompanyService: RewardsB2bCompanyService,
        });

        //Common api calls
        isCustomer$
            .pipe(
                filter((isCustomer) => isCustomer || isDesignMode),
                take(1),
                tap(() => {
                    if (isCustomerB2b) {
                        void lastValueFrom(
                            b2bCompanyService.getB2bCompanyPointBalance({
                                locale: normalizeLocale(
                                    localizationInfoProvider.getLanguageIsoCode()
                                ),
                            })
                        );
                    } else {
                        void lastValueFrom(
                            programAccountService.getRewardsProgramAccount({
                                locale: normalizeLocale(
                                    localizationInfoProvider.getLanguageIsoCode()
                                ),
                            })
                        );
                    }
                })
            )
            .subscribe();

        const branding$ = storefrontValuesService.selectRewardsBranding();

        //Rewards widgets injection
        const widgetInjectionService = new RewardsWidgetInjectionService(
            isDesignMode
        );

        const { popUpWidget, drawerWidget } = BRANDING_WIDGET_TYPES;

        const isEligibleCustomer$ =
            customerEligibilityService.selectIsEligibleCustomer();
        const isPointsProgramEnabled$ =
            storefrontValuesService.selectRewardsProgramStatus(
                PROGRAM_STATUS.pointsProgramEnabled
            );

        const isTierProgramEnabled$ =
            storefrontValuesService.selectRewardsProgramStatus(
                PROGRAM_STATUS.tiersProgramEnabled
            );
        const isReferralProgramEnabled$ =
            storefrontValuesService.selectRewardsProgramStatus(
                PROGRAM_STATUS.referralProgramEnabled
            );
        const isB2bProgramEnabled$ =
            storefrontValuesService.selectRewardsProgramStatus(
                PROGRAM_STATUS.b2bProgramEnabled
            );
        const canUseRewardPage$ = storefrontValuesService
            .selectRewardsPageWidgetsAvailability()
            .pipe(map((pageAvailability) => pageAvailability.canUseRewardPage));
        const canUseAccountUI$ = growaveStorefrontValuesService
            .selectAccountUISettings()
            .pipe(map((settings) => settings.canUseAccountUI));
        const isLoyaltyQREnabled$ = storefrontValuesService
            .selectRewardsPosSettings()
            .pipe(map((settings) => settings.isEnabled));
        const isPopUpWidgetLauncherVisible$ = branding$.pipe(
            map((branding) =>
                isPopUpWidgetLauncherVisible(
                    branding.popUpWidget.launcher.visibility.device
                )
            )
        );
        const isNotRewardsPage$ = storefrontValuesService
            .selectRewardsGeneralSettings()
            .pipe(
                map(
                    (settings) =>
                        !isRewardsPage(
                            locationController.getHref(),
                            urlsBuilder.createRewardsPageUrl(
                                settings.rewardPageHandle ?? ""
                            )
                        )
                )
            );
        const isTypePopUpWidget$ = branding$.pipe(
            map((branding) => branding.general.widget.type === popUpWidget)
        );
        const isTypeDrawerWidget$ = branding$.pipe(
            map((branding) => branding.general.widget.type === drawerWidget)
        );

        const isCustomersCanSelfRefund$ = storefrontValuesService
            .selectRewardsGeneralSettings()
            .pipe(map((settings) => settings.isCustomersCanSelfRefund));

        widgetInjectionService.registerInjectors([
            this.createPopUpWidgetInjectorConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isPopUpWidgetLauncherVisible$,
                    isNotRewardsPage$,
                    isTypePopUpWidget$,
                    isCustomerB2b ? isB2bProgramEnabled$ : of(true),
                ],
                designModeConditions: [
                    isTypePopUpWidget$,
                    isPopUpWidgetLauncherVisible$,
                ],
                injectorCommonConfig,
                branding$,
            }),
            this.createDrawerWidgetInjectorConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isNotRewardsPage$,
                    isTypeDrawerWidget$,
                    isCustomerB2b ? isB2bProgramEnabled$ : of(true),
                ],
                injectorCommonConfig,
                designModeConditions: [isTypeDrawerWidget$],
            }),
            this.createPointsWidgetsInjectorConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomerB2b
                        ? isB2bProgramEnabled$
                        : isPointsProgramEnabled$,
                    canUseRewardPage$,
                ],
                injectorCommonConfig,
                isCustomerB2b,
            }),
            this.createEarnPointsWidgetConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isPointsProgramEnabled$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createVipTiersWidgetsConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isTierProgramEnabled$,
                    canUseRewardPage$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createVipTiersProgressWidgetsConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isTierProgramEnabled$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createLoyaltyInfoPageWidgetConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    canUseAccountUI$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createQrCodeModalWidgetConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    canUseAccountUI$,
                    isLoyaltyQREnabled$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createRefundDiscountModalWidgetConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    isCustomersCanSelfRefund$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createAvailableRewardsWidgetsConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    canUseRewardPage$,
                    isCustomerB2b ? isB2bProgramEnabled$ : of(true),
                ],
                injectorCommonConfig,
                isCustomerB2b,
            }),
            this.createAvailableStoreCreditAndHistoryWidgetsConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    canUseRewardPage$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createCustomerPointsBalanceInlineWidgetConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createReferralProgramWidgetsConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    customerEligibilityService.selectCanUseReferralProgram(),
                    canUseRewardPage$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createReferralsPageWidgetConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    customerEligibilityService.selectCanUseReferralProgram(),
                    canUseRewardPage$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createHubAndAvailableCartWidgetConfig({
                shopConditions: [isEligibleCustomer$],
                injectorCommonConfig,
                isCustomerB2b,
            }),
            this.createFaqPageWidgetConfig({
                shopConditions: [
                    isEligibleCustomer$,
                    canUseRewardPage$,
                    of(!isCustomerB2b),
                ],
                injectorCommonConfig,
            }),
            this.createJoinNowModalWidgetConfig({
                shopConditions: [
                    isCustomer$.pipe(map((isCustomer) => !isCustomer)),
                    isReferralProgramEnabled$,
                    of(!isCustomerB2b),
                    of(!!referralContextService.getReferralContext()),
                ],
                injectorCommonConfig,
            }),
            this.createEmptyStatePageWidgetConfig({
                shopConditions: [
                    isEligibleCustomer$.pipe(
                        map((isEligibleCustomer) => !isEligibleCustomer)
                    ),
                ],
                injectorCommonConfig,
            }),
        ]);

        widgetInjectionService.injectWidgets();

        //Subscribes to SDK methods should be placed before SDK module registration
        subscribeToSdkMethods({
            programAccountApiService,
            earningRulesApiService,
            spendingRulesApiService,
            customerRewardsApiService,
            vipTiersApiService,
            verificationTokenApiService,
            modalManager,
            dynamicImportManager: this.dynamicImportManager,
            productsService,
        });

        // Prepare SDK module for dynamic import
        void (async () => {
            const { sdk, sdkDynamicModulesManager, sdkMethodsCollector } =
                sdkModule.exports;

            const RewardsSdk = await sdkDynamicModulesManager.import(
                SDK_MODULES.REWARDS
            );
            const rewardsSdk = new RewardsSdk(
                sdkMethodsCollector,
                productsQuery
            );
            sdk.registerModule(SDK_MODULES.REWARDS, rewardsSdk);
        })();

        baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            App.Rewards,
            MODULE_STATUS.INITIALIZED
        );

        return this;
    }

    private createPopUpWidgetInjectorConfig({
        shopConditions,
        designModeConditions,
        branding$,
        injectorCommonConfig,
    }: WidgetInjectorParams & {
        designModeConditions: InjectorDefinition["designModeConditions"];
        branding$: Observable<RewardsStorefrontValuesBrandingEntity>;
    }): InjectorDefinition {
        return {
            shopConditions,
            designModeConditions,
            startInjector: () => {
                new RewardsPopUpWidgetInjector({
                    placeholderSelector:
                        this.config.popUpWidgetWrapperPlaceholder,
                    ...injectorCommonConfig,
                    branding$,
                }).start();

                new RewardsVerifyAccountModalWidgetInjector({
                    placeholderSelector:
                        this.config.verifyAccountModalWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start();
            },
            injectInDesignMode: true,
        };
    }

    private createDrawerWidgetInjectorConfig({
        shopConditions,
        designModeConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams & {
        designModeConditions: InjectorDefinition["designModeConditions"];
    }): InjectorDefinition {
        return {
            shopConditions,
            designModeConditions,
            startInjector: () =>
                new RewardsDrawerWidgetInjector({
                    placeholderSelector: this.config.drawerWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: true,
        };
    }

    private createPointsWidgetsInjectorConfig({
        shopConditions,
        injectorCommonConfig,
        isCustomerB2b,
    }: WidgetInjectorParams & {
        isCustomerB2b: boolean;
    }): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () => {
                new RewardsWaysToEarnPageWidgetInjector({
                    placeholderSelector:
                        this.config.waysToEarnPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                    isCustomerB2b,
                }).start();

                new RewardsWaysToEarnPageWidgetModalInjector({
                    placeholderSelector:
                        this.config.waysToEarnPageWidgetModalPlaceholder,
                    ...injectorCommonConfig,
                    isCustomerB2b,
                }).start();

                new RewardsWaysToSpendPageWidgetInjector({
                    placeholderSelector:
                        this.config.waysToSpendPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                    isCustomerB2b,
                }).start();

                new RewardsWaysToSpendPageWidgetModalInjector({
                    placeholderSelector:
                        this.config.waysToSpendPageWidgetModalPlaceholder,
                    ...injectorCommonConfig,
                    isCustomerB2b,
                }).start();

                new FreeProductSliderWidgetInjector({
                    placeholderSelector:
                        this.config.freeProductSliderWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start();
            },
            injectInDesignMode: true,
        };
    }

    private createEarnPointsWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () => {
                new RewardsEarnPointsProductPageWidgetInjector({
                    placeholderSelector:
                        this.config.earnPointsProductPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                    attributesObserver: this.attributesObserver,
                }).start();

                new RewardsEarnPointsCartWidgetInjector({
                    placeholderSelector:
                        this.config.earnPointsCartWidgetPlaceholder,
                    ...injectorCommonConfig,
                    attributesObserver: this.attributesObserver,
                }).start();
            },
            injectInDesignMode: true,
        };
    }

    private createVipTiersWidgetsConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () => {
                new RewardsVipTiersPageWidgetInjector({
                    placeholderSelector:
                        this.config.vipTiersPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start();

                new RewardsVipTiersCardWidgetInjector({
                    placeholderSelector:
                        this.config.vipTiersCardWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start();
            },
            injectInDesignMode: true,
        };
    }

    private createVipTiersProgressWidgetsConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () => {
                new RewardsVipTiersSingleProgressWidgetInjector({
                    placeholderSelector:
                        this.config.vipTiersSingleProgressWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start();

                new RewardsVipTiersMultiProgressWidgetInjector({
                    placeholderSelector:
                        this.config.vipTiersMultiProgressWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start();
            },
            injectInDesignMode: true,
        };
    }

    private createLoyaltyInfoPageWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsLoyaltyInfoPageWidgetInjector({
                    placeholderSelector:
                        this.config.loyaltyInfoPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: true,
        };
    }

    private createQrCodeModalWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsQrCodeModalWidgetInjector({
                    placeholderSelector:
                        this.config.qrCodeModalWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: true,
        };
    }

    private createRefundDiscountModalWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsRefundDiscountModalWidgetInjector({
                    placeholderSelector:
                        this.config.refundDiscountModalWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: true,
        };
    }

    private createAvailableRewardsWidgetsConfig({
        shopConditions,
        injectorCommonConfig,
        isCustomerB2b,
    }: WidgetInjectorParams & {
        isCustomerB2b: boolean;
    }): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () => {
                new RewardsAvailableRewardsPageWidgetInjector({
                    placeholderSelector:
                        this.config.availableRewardsPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                    isCustomerB2b,
                }).start();

                new RewardsAvailableRewardsPageWidgetModalInjector({
                    placeholderSelector:
                        this.config.availableRewardsPageWidgetModalPlaceholder,
                    ...injectorCommonConfig,
                    isCustomerB2b,
                }).start();
            },
            injectInDesignMode: true,
        };
    }

    private createAvailableStoreCreditAndHistoryWidgetsConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () => {
                new RewardsAvailableStoreCreditPageWidgetModalInjector({
                    placeholderSelector:
                        this.config.availableRewardsPageWidgetModalPlaceholder,
                    ...injectorCommonConfig,
                }).start();
                new RewardsHistoryPageWidgetInjector({
                    placeholderSelector:
                        this.config.historyPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start();
            },
            injectInDesignMode: true,
        };
    }

    private createCustomerPointsBalanceInlineWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsCustomerPointsBalanceInlineWidgetInjector({
                    placeholderSelector:
                        this.config
                            .customerPointsBalanceInlineWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: true,
        };
    }

    private createReferralProgramWidgetsConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsReferralProgramPageWidgetInjector({
                    placeholderSelector:
                        this.config.referralProgramPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: true,
        };
    }

    private createReferralsPageWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsReferralsPageWidgetInjector({
                    placeholderSelector:
                        this.config.referralsPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: true,
        };
    }

    private createHubAndAvailableCartWidgetConfig({
        shopConditions,
        injectorCommonConfig,
        isCustomerB2b,
    }: WidgetInjectorParams & {
        isCustomerB2b: boolean;
    }): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () => {
                new RewardsHubWidgetInjector({
                    placeholderSelector: this.config.hubWidgetPlaceholder,
                    ...injectorCommonConfig,
                    isCustomerB2b,
                }).start();

                new AvailableRewardsCartWidgetInjector({
                    placeholderSelector:
                        this.config.availableRewardsCartWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start();
            },
            injectInDesignMode: true,
        };
    }

    private createFaqPageWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsFaqPageWidgetInjector({
                    placeholderSelector: this.config.faqPageWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: true,
        };
    }

    private createJoinNowModalWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsJoinNowModalWidgetInjector({
                    placeholderSelector:
                        this.config.joinNowModalWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: false,
        };
    }

    private createEmptyStatePageWidgetConfig({
        shopConditions,
        injectorCommonConfig,
    }: WidgetInjectorParams): InjectorDefinition {
        return {
            shopConditions,
            startInjector: () =>
                new RewardsEmptyStatePageWidgetInjector({
                    placeholderSelector:
                        this.config.emptyStatePageWidgetPlaceholder,
                    ...injectorCommonConfig,
                }).start(),
            injectInDesignMode: false,
        };
    }
}
